<script setup>
  import { ref, computed, inject, watch, watchEffect } from 'vue';

  import { useUserStore } from "@/stores/UserStore";
  import { useSpotStore } from "@/stores/SpotStore";
  import { useCompleteReportStore } from "@/stores/CompleteReportStore";

  import { useMediaQuery } from '@/composables/useMediaQuery';

  import DataContainer from "@/components/DataContainer.vue";
  import WindGraphBasic from '@/components/WindGraphBasic.vue';
  import WindGraphDetailed from '@/components/WindGraphDetailed.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";
  import ToggleList from '@/components/controls/ToggleList.vue';
  import ToggleItem from '@/components/controls/ToggleItem.vue';

  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const emit = defineEmits(['graphType:change'])
  const content = defineModel('content');

  const userStore = useUserStore();
  const spotStore = useSpotStore();
  const completeReportStore = useCompleteReportStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const isNCWind = inject('isNCWind');

  const graphType = ref(!isDesktop.value || $WEB_VIEW ? 'basic' : completeReportStore.windGraphStartingView);
  const basicGraph = ref(null);
  const basicGraphZoomLevel = ref(12);
  const basicGraphNoModelSelected = ref(false);
  const detailedGraph = ref(null);

  const showNCWindToggle = computed(() => userStore.showNCWindToggle && spotStore.isNCWindCapable);

  async function handleNCWindToggle(value) {
    if (!content.value) return;

    content.value.useNCWindData(value);  
  }

  watchEffect(() => {
    content.value = graphType.value === 'basic' ? basicGraph.value : detailedGraph.value;
  })

  watchEffect(() => {
    if (basicGraphNoModelSelected.value && basicGraphZoomLevel.value === 24) {
      basicGraphZoomLevel.value = 12;
    }
  })

  watchEffect(() => emit('graphType:change', graphType.value));

  watch(basicGraphZoomLevel, (newZoom) => {
    if (!basicGraph.value || !basicGraph.value.isLoaded) return;
  
    basicGraph.value.zoom(newZoom);
  });

  window.$EventBus.addEventListener('cosmos-graph-rendered', (e) => {
    if (!e.detail.selectedModels) return;
    
    basicGraphNoModelSelected.value = e.detail.selectedModels.length === 0;
  })
</script>

<template>
  <DataContainer style="width: 100%; height: 100%;">
    <div
      class="jwx-data-container__toolbar"
      ref="toolbarContainer"
    >
      <div class="jwx-data-container__controls-left">
        <SegmentedControl
          v-if="graphType === 'basic'"
          v-model.number="basicGraphZoomLevel"
          label="Select the time period length to display on the graph."
          style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px;"
        >
          <SegmentedControlButton :value="4">4h</SegmentedControlButton>
          <SegmentedControlButton :value="12">12h</SegmentedControlButton>
          <SegmentedControlButton :value="24" :disabled="basicGraphNoModelSelected">24h</SegmentedControlButton>
        </SegmentedControl>        
      </div>
      <div class="jwx-data-container__controls-right">
        <div v-if="showNCWindToggle" class="jwx-data-container__control-group jwx-data-container__control-group--toggle">
          <ToggleList>
            <ToggleItem
              id="show-nc-wind"
              v-model="spotStore.ncWindDataEnabled"
              :onChangeCallback="handleNCWindToggle"
              :smallText="true"
            >
             Nearcast&trade; Wind
            </ToggleItem>
          </ToggleList>
        </div>
  
        <SegmentedControl
          v-if="isDesktop && !$WEB_VIEW"
          v-model="graphType"
          label="Choose to display the Basic or Detailed Graph."
          style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px; margin-left: auto;"
        >
          <SegmentedControlButton value="basic">Basic</SegmentedControlButton>
          <SegmentedControlButton value="detailed">Detailed</SegmentedControlButton>
        </SegmentedControl>
      </div>
    </div>

    <WindGraphBasic
      v-if="graphType === 'basic'"
      v-model="basicGraph"
      v-model:ncWindDataEnabled="spotStore.ncWindDataEnabled"
      :isNCWind="isNCWind"
     />
    <WindGraphDetailed
      v-if="graphType === 'detailed'"
      v-model="detailedGraph"
      v-model:ncWindDataEnabled="spotStore.ncWindDataEnabled"
      :isNCWind="isNCWind"
     />
  </DataContainer>
</template>